import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Button, 
  Stack,
  Link
} from '@mui/material';
import { motion } from 'framer-motion';

// Icons
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PrintIcon from '@mui/icons-material/Print';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface BookingConfirmationProps {
  bookingReference: string;
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({ bookingReference }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircleOutlineIcon 
          color="success" 
          sx={{ fontSize: 80, mb: 2 }} 
        />
        <Typography variant="h4" gutterBottom>
          Booking Confirmed!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          Thank you for choosing The United Travel
        </Typography>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Booking Reference: <strong>{bookingReference}</strong>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          A confirmation email has been sent to your registered email address
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Next Steps
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              1. Check Your Email
            </Typography>
            <Typography variant="body1">
              We've sent a detailed confirmation of your booking to your email address. Please review all details to ensure everything is correct.
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              2. Driver Confirmation
            </Typography>
            <Typography variant="body1">
              Your assigned driver will contact you 24 hours before your scheduled pickup to confirm all arrangements.
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              3. Prepare for Your Journey
            </Typography>
            <Typography variant="body1">
              On the day of service, your chauffeur will arrive 10 minutes before your scheduled pickup time. You'll receive an SMS notification upon their arrival.
            </Typography>
          </Box>
        </Stack>
      </Paper>
      
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 2, bgcolor: 'primary.light' }}>
        <Typography variant="h6" gutterBottom>
          Need to Make Changes?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If you need to modify or cancel your booking, please contact our customer service team as soon as possible. Changes made within 24 hours of the scheduled service may incur fees.
        </Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2}
          justifyContent="center"
        >
          <Button 
            variant="contained" 
            fullWidth 
            href="tel:+916351003667"
            sx={{ py: 1.5 }}
          >
            Call Us: +91 6351003667
          </Button>
          <Button 
            variant="outlined" 
            fullWidth 
            href="mailto:contact.theunitedtravels@gmail.com"
            sx={{ py: 1.5 }}
          >
            Email Support
          </Button>
        </Stack>
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          Print Confirmation
        </Button>
        
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          component={Link}
          href="/dashboard"
        >
          Go to My Bookings
        </Button>
      </Box>
      
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Have questions about your booking? Contact our 24/7 customer service at +91 6351003667 
          or email us at <Link href="mailto:contact.theunitedtravels@gmail.com">contact.theunitedtravels@gmail.com</Link>
        </Typography>
      </Box>
    </motion.div>
  );
};

export default BookingConfirmation; 